import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
// import * as rudderanalytics from 'rudder-sdk-js'
import ScreenLoading from 'components/ScreenLoading'
import Modal from 'components/Modal'
import { IOrganization } from 'types'
import TagManager from 'react-gtm-module'

interface IOrganizationPickerProps {
  organizations: IOrganization[]
  isLoadingOrganization: boolean
  organizationSlug: string
  currentOrganization: IOrganization | null
  page: string
  onPick?: (organization: IOrganization) => void
  style?: React.CSSProperties
  showLabel?: boolean
}

const OrganizationPicker: React.FC<IOrganizationPickerProps> = ({
  organizations,
  isLoadingOrganization,
  organizationSlug,
  currentOrganization,
  page,
  onPick = null,
  style = {},
  showLabel = false,
}) => {
  const [pickOrganizationOpen, setPickOrganizationOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const history = useHistory()
  const orgPickerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleClickInside = (e) => {
    e.stopPropagation()
  }

  const handleClickOrganizationMenu = (e) => {
    e.stopPropagation()
    if (!pickOrganizationOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      setSearchQuery('')
    }
    setPickOrganizationOpen(!pickOrganizationOpen)
  }

  const handleAddOrganization = () => {
    // rudderanalytics.track('new_organization_button_clicked')
    TagManager.dataLayer({ dataLayer: { event: `new_organization_button_clicked`, is_custom_track: true } })

    history.push('/create-organization')
  }

  const handleClickOutside = useCallback(
      (e) => {
        if (orgPickerRef.current && !orgPickerRef.current.contains(e.target)) {
          setPickOrganizationOpen(false)
          document.removeEventListener('mousedown', handleClickOutside)
        }
      },
      [setPickOrganizationOpen]
    )

  // interface IRoutes {
  //   [key: string]: string[]
  // }

  // const reverseRoutes = (routes: IRoutes) => {
  //   let reverseRoutes: IRoutes = {}
  //   for (let key in routes) {
  //     routes[key].forEach((route) => {
  //       reverseRoutes[route] = [key]
  //     })
  //   }
  //   return reverseRoutes
  // }

  const handleChangeOrganization = (organization) => {
    setPickOrganizationOpen(false)
    if (onPick) {
      onPick(organization)
      return
    }
    
    localStorage.setItem("currentOrganization", organization.slug)
    let route = `/organization/${organization.slug}/`
    if (organization.is_demo) {
      history.push(route)
      return
    }

    // const routes = {
    //   add: ['add-source'],
    //   sources: ['sources'],
    //   clusters: ['clusters'],
    //   'account-settings': ['account-settings'],
    //   settings: ['settings'],
    //   dashboard: ['dashboard'],
    //   demo: ['demo'],
    //   competitors: ['competitors'],
    //   pagespeed: ['pagespeed'],
    //   dataload: ['dataload'],
    //   notifications: ['notifications'],
    //   'billing/details': ['billing', 'billingDetails', 'billingCheckout'],
    // }
    // const reversedRoutes = reverseRoutes(routes);
    // history.push(route + reversedRoutes[page][0])

    history.push("/")
  }

  const filteredOrganizations = organizations?.filter((org) =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const organizationListContent = (
    <>
      {!onPick && (
        <button
          onClick={handleAddOrganization}
          className="link add-organization"
        >
          <span className="enlarge">+</span>Add new organization
        </button>
      )}
      <span className="subtitle-text">Select organization</span>
      <div className="search-container">
        <span className="material-icons search-icon">search</span>
        <input
          type="text"
          placeholder="Search organizations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      {!isLoadingOrganization ? (
        <ul>
          {filteredOrganizations?.length > 0 ? (
            filteredOrganizations.map((organization) => (
              <li
                onClick={() => handleChangeOrganization(organization)}
                key={organization.slug}
                className={`${
                  organization.slug === organizationSlug ? 'selected' : ''
                }`}
              >
                {organization.slug === organizationSlug ? (
                  <span className="material-icons-outlined check-circle checked">
                    radio_button_checked
                  </span>
                ) : (
                  <span className="material-icons-outlined check-circle unchecked">
                    radio_button_unchecked
                  </span>
                )}
                <span className="organization-name">
                  {organization.name}
                </span>
              </li>
            ))
          ) : (
            <li className="no-results">No organizations found</li>
          )}
        </ul>
      ) : (
        <ScreenLoading />
      )}
    </>
  )

  if (isMobile && pickOrganizationOpen) {
    return (
      <Modal
        show={true}
        onHide={() => setPickOrganizationOpen(false)}
        className="organization-picker-modal"
      >
        <div className="organization-picker-modal-content">
          <div className="modal-header">
            <h2>Switch Organization</h2>
            <button className="close-button" onClick={() => setPickOrganizationOpen(false)}>
              <span className="material-icons">close</span>
            </button>
          </div>
          <div className="modal-body">
            {organizationListContent}
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <>
      <div ref={orgPickerRef} onClick={handleClickInside} className="organizations-menu" style={style}>
        {currentOrganization && currentOrganization.name !== null ? (
          <div
            onClick={handleClickOrganizationMenu}
            className={`pick-organization ${
              pickOrganizationOpen ? 'open' : ''
            }`}
            style={showLabel ? {height: "2.7rem"} : {}}
          >
            {showLabel && (<div className="input-header"><label>Organization</label></div>)}
            <div className="picked" style={showLabel ? {paddingTop: "1.4rem"} : {}}>
              <span>{currentOrganization.name}</span>
            </div>
            <span
              style={showLabel ? {top: "8px"} : {}}
              className={`material-icons arrow non-selectable ${
                pickOrganizationOpen && 'rotate'
              }`}
            >
              keyboard_arrow_up
            </span>
          </div>
        ) : (
          <ScreenLoading />
        )}

        {pickOrganizationOpen && (
          <div className="drop-down-organizations" style={showLabel ? {top: "calc(3.7rem - 15px"} : {}}>
            {organizationListContent}
          </div>
        )}
      </div>
    </>
  )
}

export default OrganizationPicker
